import { useAtom } from 'jotai';
import { type MouseEvent } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, NavItem } from '@optiaxiom/react';

import { stickyboardDrawerVisibleAtom } from '@frontend/atoms/stickyboardAtoms';

type Props = {
  icon: JSX.Element;
  label: string;
};

/**
 * Left-rail entry for Stickyboard. Unlike the generic NavigationItem (which
 * navigates on click), this distinguishes:
 *   - single click → toggle the drawer overlay (the common, low-friction path)
 *   - double click → open the full `/stickyboard` page
 *
 * Gating (feature flag + capability + permission) is applied by the caller
 * (LeftNavbar's Stickyboard group), so this component renders unconditionally.
 */
export function StickyboardNavItem({ icon, label }: Props) {
  const navigate = useNavigate();
  const { instanceId } = useParams();
  const location = useLocation();
  const [drawerVisible, setDrawerVisible] = useAtom(stickyboardDrawerVisibleAtom);

  const onStickyboardRoute = location.pathname.split('/')[3] === 'stickyboard';

  const handleClick = (e: MouseEvent) => {
    // The second click of a double click reports detail >= 2; let
    // onDoubleClick own that gesture so we don't toggle the drawer twice.
    if (e.detail > 1) return;
    setDrawerVisible(!drawerVisible);
  };

  const handleDoubleClick = () => {
    setDrawerVisible(false);
    navigate(`/instances/${instanceId}/stickyboard`);
  };

  return (
    <Box
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      data-testid="stickyboard-nav-item"
      cursor="pointer"
    >
      <NavItem active={drawerVisible || onStickyboardRoute} icon={icon}>
        {label}
      </NavItem>
    </Box>
  );
}
